import { supabase } from "@/integrations/supabase/client";
import { 
  DigitalContent, 
  DigitalContentCreateData, 
  DigitalContentUpdateData,
  DigitalContentStats,
  MentorDigitalContentStats
} from "@/types/digitalContent";

/**
 * Criar um novo conteúdo digital
 */
export async function createDigitalContent(contentData: DigitalContentCreateData): Promise<DigitalContent> {
  console.log("📚 [createDigitalContent] Função iniciada com dados:", contentData);
  
  try {
    // Buscar dados do mentor atual
    console.log("📚 [createDigitalContent] Buscando dados do mentor...");
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor_info")
      .select("id")
      .eq("profile_id", (await supabase.auth.getUser()).data.user?.id || "")
      .single();

    if (mentorError) {
      console.error("📚 [createDigitalContent] Erro ao buscar mentor:", mentorError);
      throw mentorError;
    }

    const mentorId = mentorData.id;
    console.log("📚 [createDigitalContent] Mentor ID encontrado:", mentorId);

    // Separar arquivos adicionais dos dados principais
    const { additional_files, ...mainContentData } = contentData;

    // Inserir conteúdo principal
    console.log("📚 [createDigitalContent] Inserindo conteúdo no banco...");
    const { data, error } = await supabase
      .from("digital_contents")
      .insert({
        ...mainContentData,
        mentor_id: mentorId,
      })
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .single();

    if (error) {
      console.error("📚 [createDigitalContent] Erro ao inserir no banco:", error);
      throw error;
    }
    
    console.log("📚 [createDigitalContent] Conteúdo inserido com sucesso:", data);

    // Inserir arquivos adicionais se existirem
    if (additional_files && additional_files.length > 0) {
      console.log("📚 [createDigitalContent] Inserindo arquivos adicionais...");
      
      const filesData = additional_files.map(file => ({
        ...file,
        digital_content_id: data.id
      }));

      const { error: filesError } = await supabase
        .from("digital_content_files")
        .insert(filesData);

      if (filesError) {
        console.error("📚 [createDigitalContent] Erro ao inserir arquivos:", filesError);
        // Não falhar a operação por causa dos arquivos adicionais
      } else {
        console.log("📚 [createDigitalContent] Arquivos adicionais inseridos com sucesso");
      }
    }

    // Mapear dados para formato padronizado
    const mappedContent = mapDigitalContentData(data);
    
    console.log("📚 [createDigitalContent] Conteúdo criado com sucesso:", mappedContent);
    return mappedContent;

  } catch (error) {
    console.error("📚 [createDigitalContent] Erro geral:", error);
    throw error;
  }
}

/**
 * Buscar todos os conteúdos digitais do mentor atual
 */
export async function getDigitalContents(): Promise<DigitalContent[]> {
  try {
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor_info")
      .select("id")
      .eq("profile_id", (await supabase.auth.getUser()).data.user?.id || "")
      .single();

    if (mentorError) {
      console.error("Error fetching mentor info:", mentorError);
      throw mentorError;
    }

    const mentorId = mentorData.id;

    const { data, error } = await supabase
      .from("digital_contents")
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .eq("mentor_id", mentorId)
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching digital contents:", error);
      throw error;
    }

    // Mapear dados para formato padronizado
    const mappedData = (data || []).map(mapDigitalContentData);

    return mappedData;
  } catch (error) {
    console.error("Error in getDigitalContents:", error);
    throw error;
  }
}

/**
 * Atualizar um conteúdo digital existente
 */
export async function updateDigitalContent(
  id: string,
  contentData: DigitalContentUpdateData
): Promise<DigitalContent> {
  try {
    console.log('💾 [updateDigitalContent] Atualizando conteúdo ID:', id);
    console.log('💾 [updateDigitalContent] Dados para atualização:', contentData);
    
    const { data, error } = await supabase
      .from("digital_contents")
      .update(contentData)
      .eq("id", id)
      .select(`
        *,
        mentor:mentor_id (
          id, 
          image_url, 
          profile_id,
          profiles:profile_id (
            id,
            nickname,
            slug
          )
        ),
        game:game_id (
          id, 
          name, 
          cover_url
        )
      `)
      .single();

    if (error) {
      console.error("💾 [updateDigitalContent] Erro ao atualizar:", error);
      throw error;
    }

    console.log('💾 [updateDigitalContent] Conteúdo atualizado no banco:', data);
    console.log('💾 [updateDigitalContent] Nova cover_image_url:', data.cover_image_url);

    const mappedData = mapDigitalContentData(data);
    console.log('💾 [updateDigitalContent] Dados mapeados:', mappedData);
    
    return mappedData;
  } catch (error) {
    console.error("💾 [updateDigitalContent] Erro geral:", error);
    throw error;
  }
}

/**
 * Deletar um conteúdo digital
 */
export async function deleteDigitalContent(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from("digital_contents")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting digital content:", error);
      throw error;
    }

    console.log("Digital content deleted successfully:", id);
  } catch (error) {
    console.error("Error in deleteDigitalContent:", error);
    throw error;
  }
}

/**
 * Buscar estatísticas de um conteúdo digital
 */
export async function getDigitalContentStats(contentId: string): Promise<DigitalContentStats> {
  try {
    const { data, error } = await supabase
      .rpc('get_digital_content_stats', { content_id: contentId });

    if (error) {
      console.error("Error fetching digital content stats:", error);
      throw error;
    }

    return data || {
      total_purchases: 0,
      total_revenue_cents: 0,
      average_rating: 0,
      total_reviews: 0,
      total_downloads: 0
    };
  } catch (error) {
    console.error("Error in getDigitalContentStats:", error);
    throw error;
  }
}

/**
 * Buscar estatísticas gerais do mentor sobre conteúdos digitais
 */
export async function getMentorDigitalContentStats(): Promise<MentorDigitalContentStats> {
  try {
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor_info")
      .select("id")
      .eq("profile_id", (await supabase.auth.getUser()).data.user?.id || "")
      .single();

    if (mentorError) {
      console.error("Error fetching mentor info:", mentorError);
      throw mentorError;
    }

    const mentorId = mentorData.id;

    // Buscar estatísticas básicas
    const { data: basicStats, error: basicError } = await supabase
      .from("digital_contents")
      .select(`
        id,
        content_type,
        price_cents,
        download_count,
        created_at
      `)
      .eq("mentor_id", mentorId)
      .eq("is_active", true);

    if (basicError) {
      console.error("Error fetching basic stats:", basicError);
      throw basicError;
    }

    // Buscar estatísticas de vendas
    const { data: salesStats, error: salesError } = await supabase
      .from("digital_content_purchases")
      .select(`
        purchase_price_cents,
        download_count,
        created_at,
        digital_content:digital_content_id!inner(mentor_id)
      `)
      .eq("payment_status", "completed")
      .eq("digital_content.mentor_id", mentorId);

    if (salesError) {
      console.error("Error fetching sales stats:", salesError);
      throw salesError;
    }

    // Processar estatísticas
    const totalContents = basicStats?.length || 0;
    const totalSales = salesStats?.length || 0;
    const totalRevenueCents = salesStats?.reduce((sum, sale) => sum + sale.purchase_price_cents, 0) || 0;
    const totalDownloads = salesStats?.reduce((sum, sale) => sum + sale.download_count, 0) || 0;

    // Contar por tipo de conteúdo
    const contentsByType = basicStats?.reduce((acc, content) => {
      acc[content.content_type] = (acc[content.content_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};

    // Receita mensal (últimos 12 meses)
    const monthlyRevenue = processMonthlyRevenue(salesStats || []);

    return {
      total_contents: totalContents,
      total_sales: totalSales,
      total_revenue_cents: totalRevenueCents,
      average_rating: 0, // TODO: Implementar quando tiver avaliações
      total_downloads: totalDownloads,
      contents_by_type: contentsByType as any,
      monthly_revenue: monthlyRevenue
    };

  } catch (error) {
    console.error("Error in getMentorDigitalContentStats:", error);
    throw error;
  }
}

/**
 * Atualizar ordem de exibição dos conteúdos
 */
export async function updateDigitalContentsOrder(contentIds: string[]): Promise<void> {
  try {
    const updates = contentIds.map((id, index) => ({
      id,
      display_order: index
    }));

    for (const update of updates) {
      const { error } = await supabase
        .from("digital_contents")
        .update({ display_order: update.display_order })
        .eq("id", update.id);

      if (error) {
        console.error(`Error updating order for content ${update.id}:`, error);
        throw error;
      }
    }

    console.log("Digital contents order updated successfully");
  } catch (error) {
    console.error("Error in updateDigitalContentsOrder:", error);
    throw error;
  }
}

// Função auxiliar para mapear dados do banco para o formato padronizado
function mapDigitalContentData(item: any): DigitalContent {
  const mentorName = item.mentor?.profiles?.nickname || 
                    `Mentor ${item.mentor_id?.substring(0, 4)}`;
  
  return {
    id: item.id,
    mentor_id: item.mentor_id,
    title: item.title,
    description: item.description,
    content_type: item.content_type,
    price_cents: item.price_cents,
    cover_image_url: item.cover_image_url,
    file_url: item.file_url,
    file_size_bytes: item.file_size_bytes,
    file_name: item.file_name,
    preview_content: item.preview_content,
    tags: item.tags,
    difficulty_level: item.difficulty_level,
    estimated_duration_minutes: item.estimated_duration_minutes,
    is_active: item.is_active,
    is_featured: item.is_featured,
    display_order: item.display_order,
    download_count: item.download_count,
    game_id: item.game_id,
    created_at: item.created_at,
    updated_at: item.updated_at,
    game: item.game,
    mentor: item.mentor,
    mentor_name: mentorName,
    mentor_image_url: item.mentor?.image_url,
    mentor_slug: item.mentor?.profiles?.slug
  };
}

// Função auxiliar para processar receita mensal
function processMonthlyRevenue(salesData: any[]): Array<{month: string, revenue_cents: number, sales_count: number}> {
  const monthlyData: Record<string, {revenue_cents: number, sales_count: number}> = {};
  
  salesData.forEach(sale => {
    const date = new Date(sale.created_at);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = { revenue_cents: 0, sales_count: 0 };
    }
    
    monthlyData[monthKey].revenue_cents += sale.purchase_price_cents;
    monthlyData[monthKey].sales_count += 1;
  });
  
  // Converter para array e ordenar por mês
  return Object.entries(monthlyData)
    .map(([month, data]) => ({
      month,
      revenue_cents: data.revenue_cents,
      sales_count: data.sales_count
    }))
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-12); // Últimos 12 meses
} 